/* ─────────────────────────────────────────────────────────────────────────
   Product page behaviour: gallery, the fit question, the sticky bar and
   the size sheet. No dependencies.

   Everything here is an enhancement. Without JS the form still posts the
   right variant, because the size circles are radios whose values are
   variant ids — there is no <select> to hydrate and nothing to wire up.
   ───────────────────────────────────────────────────────────────────────── */
(() => {
  'use strict';
  const root = document.querySelector('[data-sadil-product]');
  if (!root) return;

  const $ = (s, c = root) => c.querySelector(s);
  const $$ = (s, c = root) => [...c.querySelectorAll(s)];

  /* ── Gallery ──────────────────────────────────────────────────────────── */
  const track = $('[data-gallery-track]');
  if (track) {
    const items = $$('[data-gallery-item]');
    const markers = $$('.sadil-gallery__marker');
    const thumbs = $$('[data-gallery-thumb]');

    const setCurrent = (i) => {
      markers.forEach((m, n) => m.classList.toggle('is-current', n === i));
      thumbs.forEach((t, n) => t.classList.toggle('is-current', n === i));
    };

    /* Which slide is showing: read it from intersection rather than from
       scrollLeft, so it is correct in RTL without a sign flip. */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const i = items.indexOf(e.target);
            if (i > -1) setCurrent(i);
            const video = e.target.querySelector('[data-gallery-video]');
            if (video && !video.src && video.dataset.src) {
              // Source attached only once the slide is actually reached.
              video.src = video.dataset.src;
              video.play().catch(() => {}); // muted autoplay may still be refused
            }
          });
        },
        { root: track, threshold: 0.6 }
      );
      items.forEach((el) => io.observe(el));
    }

    thumbs.forEach((t) =>
      t.addEventListener('click', () => {
        const i = Number(t.dataset.galleryThumb);
        items[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      })
    );
  }

  /* ── Colour swatches ──────────────────────────────────────────────────── */
  const colourLabel = $('[data-colour-label]');
  const colourInputs = $$('.sadil-colour__input');
  const galleryItems = $$('[data-gallery-item]');

  const showColour = (name, oos) => {
    if (!colourLabel) return;
    const prefix = colourLabel.dataset.prefix || '';
    colourLabel.textContent = `${prefix} · ${name}` + (oos ? ` · ${colourLabel.dataset.oosNote || ''}` : '');
  };

  colourInputs.forEach((input) => {
    input.addEventListener('change', () => {
      showColour(input.dataset.colourName, input.dataset.oos === 'true');
      /* Swap the gallery to this colour's images. The variant carries the
         media position, so this is a scroll rather than a re-render. */
      const idx = Number(input.dataset.media);
      if (!Number.isNaN(idx) && galleryItems[idx]) {
        galleryItems[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    });
  });

  /* Another colour opens the same panel the custom size uses, and puts the
     cursor in the colour field — one custom-order flow, not two. */
  $('[data-colour-other]')?.addEventListener('click', () => {
    const panel = $('[data-custom]');
    const toggle = $('[data-custom-toggle]');
    if (panel) {
      panel.hidden = false;
      toggle?.setAttribute('aria-expanded', 'true');
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      $('[data-colour-note]')?.focus({ preventScroll: true });
    }
    if (colourLabel) colourLabel.textContent = colourLabel.dataset.other || '';
  });

  /* ── Fit question → recommended size ──────────────────────────────────── */
  const result = $('[data-fit-result]');
  const sizeInputs = $$('.sadil-size__input');
  const circleFor = (v) =>
    sizeInputs.find((i) => i.dataset.sizeValue === String(v))?.nextElementSibling;

  $$('[data-band-size]').forEach((band) => {
    band.addEventListener('click', () => {
      const size = band.dataset.bandSize;
      $$('[data-band-size]').forEach((b) => b.classList.toggle('is-active', b === band));
      $$('.sadil-size__circle').forEach((c) => c.classList.remove('is-recommended'));
      const circle = circleFor(size);
      if (circle) circle.classList.add('is-recommended');
      if (result) {
        result.textContent = (result.dataset.template || 'Your size is {size}').replace('{size}', size);
        result.hidden = false;
      }
    });
  });

  /* ── Custom measurements ──────────────────────────────────────────────── */
  const customToggle = $('[data-custom-toggle]');
  const custom = $('[data-custom]');
  customToggle?.addEventListener('click', () => {
    const open = custom.hidden;
    custom.hidden = !open;
    customToggle.setAttribute('aria-expanded', String(open));
  });

  /* ── Sticky bar ───────────────────────────────────────────────────────── */
  const bar = $('[data-stickybar]');
  const title = $('[data-buy-title]');
  const stickyAdd = $('[data-sticky-add]');
  const form = $('[data-buy-form]');
  const sizes = $('[data-sizes]');

  if (bar && title && 'IntersectionObserver' in window) {
    bar.hidden = false;
    new IntersectionObserver(
      ([e]) => bar.classList.toggle('is-open', !e.isIntersecting && e.boundingClientRect.top < 0),
      { threshold: 0 }
    ).observe(title);
  }

  /* THREE entry points, ONE state machine.

     The sticky bar, the early button under the size circles, and the
     submit at the foot of the form all read the same radio group and all
     run the handler below. Nothing is duplicated: adding a fourth entry
     point means adding data-add-alt to it and nothing else.

     None of them is ever DISABLED. That is deliberate and predates this:
     a dead button explains nothing, so with no size chosen every entry
     point reads اختاري المقاس and sends the visitor to the circles. The
     shared state is the label and the click behaviour, not a disabled
     attribute. */
  const chosen = () => sizeInputs.find((i) => i.checked);
  const needsSize = sizeInputs.length > 0;
  const altAdds = $$('[data-add-alt], [data-sticky-add]');

  /* The availability line rides the SAME state machine, for the same
     reason the three buttons do: it answers a question about the chosen
     size, so anything that changes the chosen size has to change it too.
     Its idle text is whatever Liquid rendered — "اختاري المقاس لمعرفة
     المتوفر" on a garment with sizes — and deselecting returns to it.

     The script copies a finished sentence out of data-stock-text and
     never builds one. Arabic counted nouns have four forms; that rule is
     rendered server side in sadil-stock-phrase, once. */
  const stockEl = $('[data-stock]');
  const stockIdle = stockEl ? stockEl.textContent : '';

  const syncStock = () => {
    if (!stockEl) return;
    const c = chosen();
    stockEl.textContent = c && c.dataset.stockText ? c.dataset.stockText : stockIdle;
    const qty = c ? parseInt(c.dataset.stockQty, 10) : NaN;
    stockEl.classList.toggle('is-out', qty <= 0);
    stockEl.classList.toggle('is-low', qty > 0 && qty <= 3);
  };

  const syncAdds = () => {
    syncStock();
    const has = !needsSize || !!chosen();
    altAdds.forEach((b) => {
      if (b.disabled) return;
      /* labelAdd / labelSelect, NOT add / select: data-add is already the
         main submit button's hook, so reusing it here made [data-add]
         match this button instead of the real one. */
      const next = has ? b.dataset.labelAdd : b.dataset.labelSelect;
      if (next) b.textContent = next;
      b.classList.toggle('is-ready', has);
    });
  };
  sizeInputs.forEach((i) => i.addEventListener('change', syncAdds));
  /* A choice that carries a photo (a model's fabric) turns the gallery to
     it, the same scroll the colour swatches use. */
  sizeInputs.forEach((i) => i.addEventListener('change', () => {
    const idx = Number(i.dataset.media);
    if (i.dataset.media !== undefined && galleryItems[idx]) {
      galleryItems[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }));
  syncAdds();

  altAdds.forEach((b) =>
    b.addEventListener('click', () => {
      if (!needsSize || chosen()) {
        form?.requestSubmit ? form.requestSubmit() : form?.submit();
        return;
      }
      sizes?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      sizes?.querySelector('.sadil-size__input:not(:disabled)')?.focus({ preventScroll: true });
    })
  );

  /* ── Size chart sheet ─────────────────────────────────────────────────── */
  const sheet = $('[data-sheet]');
  if (sheet) {
    const open = () => {
      sheet.hidden = false;
      requestAnimationFrame(() => sheet.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
    };
    const close = () => {
      sheet.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      setTimeout(() => { sheet.hidden = true; }, 400);
    };
    $('[data-open-chart]')?.addEventListener('click', open);
    $$('[data-sheet-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !sheet.hidden) close();
    });
  }
})();
